/**
 * 트레이 그림(`TrayFigure`)의 **칸 배치 수와 축 계산** — 컴포넌트 파일 밖에 둔다.
 *
 * ⚠ 컴포넌트 파일에서 함수를 내보내면 빠른 새로고침이 깨진다(`react-refresh/only-export-components`).
 *   그래서 `trayAxes()` · `cellOrigin()` 을 여기로 뺐다. 그림은 여기 수를 읽기만 한다 — 값과 동작은 종전 그대로다.
 */

// traySVG 1319행 실측 — 칸 72×50 · 간격 5 · 여백 12 · 윗줄 58

/** 챔버 한 칸의 폭 · 높이. 바깥에는 `TrayFigure.tsx` 의 `TRAY_CELL_W` · `TRAY_CELL_H` 로 나간다 */
export const CELL_W = 72
export const CELL_H = 50
/** 칸 사이 간격(가로 · 세로) */
export const GAP_X = 5
export const GAP_Y = 5
/** 몸체 여백 — 첫 칸(윗줄 RSLOT)의 왼쪽 위 */
export const ORIGIN_X = 12
export const ORIGIN_Y = 12
/** 윗줄(RSLOT · 나사 구멍) 높이 — 3 × 3 격자는 그 아래에서 시작한다 */
export const TOP_ROW_H = 58

/**
 * 칸 n 의 왼쪽 위 — 🔴 **칸을 그리는 쪽과 축 라벨이 이 한 식을 같이 읽는다**(라벨이 칸 중심과 어긋날 수 없게).
 * 차례는 `cells` 순서 그대로 3열씩(`TrayFigure.tsx` 머리말의 실물 배치).
 */
export function cellOrigin(n: number): { col: number; row: number; x: number; y: number } {
  const col = n % 3
  const row = Math.floor(n / 3)
  return {
    col,
    row,
    x: ORIGIN_X + col * (CELL_W + GAP_X),
    y: ORIGIN_Y + TOP_ROW_H + row * (CELL_H + GAP_Y),
  }
}

/** 축 라벨 한 줄 — 머리(열 · `pos` = 칸 중심 x) · 옆(행 · `pos` = 칸 중심 y) */
export interface TrayAxisLabel {
  label: string
  pos: number
}

export interface TrayAxes {
  /** 위 머리 — 열마다 그 열 칸 이름의 숫자(`A1 B1 C1` → 「1」) */
  head: TrayAxisLabel[]
  /** 왼쪽 — 행마다 그 행 칸 이름의 글자(`A1 A2 A3` → 「A」) */
  side: TrayAxisLabel[]
}

const WELL_NAME = /^([A-Z]+)(\d+)$/

/**
 * **칸 이름을 그림 밖 엑셀식 축으로 푼다**(eric 2026-10-03 「A, B, C, 1, 2, 3 … 소모품처럼.. 바깥에」).
 *
 * 🔴 **축은 이름에서 읽는다 — 상수로 박지 않는다.** 칸 이름은 호출부(서버 웰 이름)가 넘기고, 그려지는 자리는
 *    `cellOrigin(n)` 이다. 한 열의 칸들이 같은 숫자 · 한 행의 칸들이 같은 글자를 가질 때만 축이 선다.
 * 🔴 **한 칸이라도 어긋나면 `null`** — 이름이 웰 꼴이 아니거나, 같은 열에 숫자가 둘이거나, 두 열이 같은 숫자면
 *    축을 세우지 않고 종전대로 칸 안에 이름을 적는다. 축을 틀리게 붙이느니 안 붙인다(소모품 판 `rackAxes()` 와 같은 법).
 */
export function trayAxes(cells: readonly { readonly slot: string }[]): TrayAxes | null {
  if (cells.length === 0) return null
  /* 열(머리) · 행(옆) 마다 «그려진 칸 중심»과 그 줄 칸들의 이름 조각 */
  const cols = new Map<number, { pos: number; names: Set<string> }>()
  const rows = new Map<number, { pos: number; names: Set<string> }>()
  const add = (
    groups: Map<number, { pos: number; names: Set<string> }>,
    key: number,
    pos: number,
    name: string,
  ) => {
    const group = groups.get(key) ?? { pos, names: new Set<string>() }
    group.names.add(name)
    groups.set(key, group)
  }
  for (const [n, cell] of cells.entries()) {
    const m = WELL_NAME.exec(cell.slot)
    const letter = m?.[1]
    const number = m?.[2]
    if (letter === undefined || number === undefined) return null
    const at = cellOrigin(n)
    add(cols, at.col, at.x + CELL_W / 2, number)
    add(rows, at.row, at.y + CELL_H / 2, letter)
  }
  const axis = (groups: Map<number, { pos: number; names: Set<string> }>) => {
    const out: TrayAxisLabel[] = []
    for (const [, group] of [...groups].sort((a, b) => a[0] - b[0])) {
      const [label] = group.names
      if (group.names.size !== 1 || label === undefined) return null
      out.push({ label, pos: group.pos })
    }
    return new Set(out.map((a) => a.label)).size === out.length ? out : null
  }
  const head = axis(cols)
  const side = axis(rows)
  return head === null || side === null ? null : { head, side }
}

/**
 * 축 라벨 여백(그림 단위) — 왼쪽 행 글자 · 위 열 숫자(콘솔 장비 탭 `chamberOutline`). `TrayFigure` 가 `viewBox` 를 음수 쪽으로 이만큼 넓힌다.
 * 콘솔 글자 상한(`figures.css` `.fig-tray__axis` 16px)이 드는 폭이다
 */
export const AXIS_LEFT = 18
export const AXIS_TOP = 20
/** 트레이 상자 폭 · 높이(그림 단위) — `TrayFigure` 의 `W` · `H` 와 같은 식 */
export const TRAY_W = ORIGIN_X * 2 + CELL_W * 3 + GAP_X * 2
export const TRAY_H = ORIGIN_Y * 2 + TOP_ROW_H + CELL_H * 3 + GAP_Y * 2
/** 축을 그린 평면도의 렌더 높이 · 트레이 테두리의 위 y · 높이(평면도 좌표) */
export const PLAN_HEIGHT = TRAY_H + AXIS_TOP
export const BODY_TOP = AXIS_TOP + ORIGIN_Y
export const BODY_HEIGHT = TRAY_H - ORIGIN_Y * 2
/** 축을 그린 평면도의 렌더 폭 · 트레이 몸체 가로 가운데(평면도 좌표 · 원점은 svg 왼쪽 위 = 축 여백 포함) */
export const PLAN_WIDTH = TRAY_W + AXIS_LEFT
export const PLAN_CENTER_X = AXIS_LEFT + TRAY_W / 2

/**
 * 칸 이름(`B2`) → 그 칸의 가로 · 세로 가운데(축을 그린 평면도 좌표 · 1 단위 = 배율 1 의 px). 모르는 이름이면 `undefined`.
 * 염색기 정면도의 피펫 툴이 **그 챔버 자리**로 가서 끝이 닿게 내려올 때 쓴다(`LineFigures.tsx` `StainingFrontLine` ·
 * eric 2026-10-08 「해당 피펫 툴이 해당 챔버 위치에 가게 해줘」)
 */
export function cellAnchor(slot: string): { x: number; y: number } | undefined {
  const row = ['A', 'B', 'C'].indexOf(slot[0] ?? '')
  const col = Number(slot[1]) - 1
  if (slot.length !== 2 || row < 0 || !(col >= 0 && col <= 2)) return undefined
  const origin = cellOrigin(row * 3 + col)
  return { x: AXIS_LEFT + origin.x + CELL_W / 2, y: AXIS_TOP + origin.y + CELL_H / 2 }
}
